import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  ConfirmLessonIssuePayload,
  LessonIssueReport,
  LessonIssueReportFilters,
  LinkLessonIssuePayload,
} from "~/types/admin/lessonIssueReports";
import type {
  ContentQualityFinding,
  ContentQualityFindingFilters,
  ContentQualityLesson,
  ContentQualityLessonFilters,
  ContentQualityPaginatedResponse,
  ContentQualityRun,
  ContentQualityRunCreatePayload,
  ContentQualityYouTubeAnnotation,
  ContentQualityYouTubeAnnotationFilters,
  ContentReviewerAccess,
  ContentReviewerInvitePayload,
  ContentReviewerInviteResponse,
  CorrectionLesson,
  CorrectionLessonDetail,
  CorrectionLessonFilters,
  CorrectionLessonFilesResponse,
  LessonEditableFilesResponse,
  MaterialRevision,
  MaterialRevisionPdfSource,
  MaterialRevisionPreviewMetadata,
  MaterialRevisionPublishPayload,
} from "~/types/admin/contentQuality";

class AdminContentQualityModule extends HttpFactory {
  async getIssueReports(
    filters: LessonIssueReportFilters = {},
  ): Promise<ContentQualityPaginatedResponse<LessonIssueReport>> {
    return await this.get(ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS, filters);
  }

  async getIssueReport(reportId: number): Promise<LessonIssueReport> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/`,
    );
  }

  async getIssueReportScreenshot(
    reportId: number,
    signal?: AbortSignal,
  ): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/screenshot/`,
      {},
      { responseType: "blob", signal },
    );
  }

  async getIssueReportSourcePdf(
    reportId: number,
    analysisId: number,
  ): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/source-pdf/`,
      { analysis_id: analysisId },
      { responseType: "blob" },
    );
  }

  async confirmIssueReport(
    reportId: number,
    payload: ConfirmLessonIssuePayload,
  ): Promise<LessonIssueReport> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/confirm/`,
      payload,
    );
  }

  async linkIssueReport(
    reportId: number,
    payload: LinkLessonIssuePayload,
  ): Promise<LessonIssueReport> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/link/`,
      payload,
    );
  }

  async ignoreIssueReport(
    reportId: number,
    reason: string,
  ): Promise<LessonIssueReport> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/ignore/`,
      { reason },
    );
  }

  async resolveIssueReport(
    reportId: number,
    reason: string,
  ): Promise<LessonIssueReport> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/resolve/`,
      { reason },
    );
  }

  async retryIssueReportAnalysis(reportId: number): Promise<LessonIssueReport> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/retry-analysis/`,
      {},
    );
  }

  async reanalyzeIssueReportCurrent(
    reportId: number,
  ): Promise<LessonIssueReport> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REPORTS}${reportId}/reanalyze-current/`,
      {},
    );
  }

  async getLessons(
    filters: ContentQualityLessonFilters = {},
  ): Promise<ContentQualityPaginatedResponse<ContentQualityLesson>> {
    return await this.get(ENDPOINTS.ADMIN.CONTENT_QUALITY.LESSONS, filters);
  }

  async getYouTubeAnnotations(
    filters: ContentQualityYouTubeAnnotationFilters = {},
  ): Promise<ContentQualityPaginatedResponse<ContentQualityYouTubeAnnotation>> {
    return await this.get(
      ENDPOINTS.ADMIN.CONTENT_QUALITY.YOUTUBE_ANNOTATIONS,
      filters,
    );
  }

  async createRun(
    payload: ContentQualityRunCreatePayload,
  ): Promise<ContentQualityRun> {
    return await this.post(ENDPOINTS.ADMIN.CONTENT_QUALITY.RUNS, payload);
  }

  async getRun(runId: number): Promise<ContentQualityRun> {
    return await this.get(`${ENDPOINTS.ADMIN.CONTENT_QUALITY.RUNS}${runId}/`);
  }

  async retryRunAnalysis(runId: number): Promise<ContentQualityRun> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.RUNS}${runId}/retry-analysis/`,
      {},
    );
  }

  async getFindings(
    filters: ContentQualityFindingFilters = {},
  ): Promise<ContentQualityPaginatedResponse<ContentQualityFinding>> {
    return await this.get(ENDPOINTS.ADMIN.CONTENT_QUALITY.FINDINGS, filters);
  }

  async ignoreFinding(findingId: number): Promise<ContentQualityFinding> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.FINDINGS}${findingId}/ignore/`,
      {},
    );
  }

  async resolveFinding(findingId: number): Promise<ContentQualityFinding> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.FINDINGS}${findingId}/resolve/`,
      {},
    );
  }

  async getCorrectionLessons(
    filters: CorrectionLessonFilters = {},
  ): Promise<ContentQualityPaginatedResponse<CorrectionLesson>> {
    return await this.get(
      ENDPOINTS.ADMIN.CONTENT_QUALITY.CORRECTION_LESSONS,
      filters,
    );
  }

  async getCorrectionLesson(lessonId: number): Promise<CorrectionLessonDetail> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.CORRECTION_LESSONS}${lessonId}/`,
    );
  }

  async downloadLessonOriginalPdf(
    lessonId: number,
    signal?: AbortSignal,
  ): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.CORRECTION_LESSONS}${lessonId}/original-pdf/`,
      {},
      { responseType: "blob", signal },
    );
  }

  async createRevision(
    lessonId: number,
    pdfFile: File,
    editableFile?: File | null,
  ): Promise<MaterialRevision> {
    const formData = new FormData();
    formData.append("lesson_id", String(lessonId));
    formData.append("pdf_file", pdfFile);
    if (editableFile) formData.append("editable_file", editableFile);
    return await this.post(ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS, formData);
  }

  async attachRevisionEditable(
    revisionId: number,
    editableFile: File,
  ): Promise<MaterialRevision> {
    const formData = new FormData();
    formData.append("editable_file", editableFile);
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/editable/`,
      formData,
    );
  }

  async downloadRevisionEditable(
    revisionId: number,
    signal?: AbortSignal,
  ): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/editable/download/`,
      {},
      { responseType: "blob", signal },
    );
  }

  async retryRevisionDriveSync(revisionId: number): Promise<MaterialRevision> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/drive-sync/retry/`,
      {},
    );
  }

  async getLessonEditableFiles(
    lessonId: number,
    signal?: AbortSignal,
  ): Promise<LessonEditableFilesResponse> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.CORRECTION_LESSONS}${lessonId}/editable-files/`,
      {},
      { signal },
    );
  }

  async downloadLessonEditableFile(
    lessonId: number,
    fileId: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.CORRECTION_LESSONS}${lessonId}/editable-files/${encodeURIComponent(fileId)}/download/`,
      {},
      { responseType: "blob", signal },
    );
  }

  async getCorrectionLessonFiles(
    lessonId: number,
    folderId?: string,
    signal?: AbortSignal,
  ): Promise<CorrectionLessonFilesResponse> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.CORRECTION_LESSONS}${lessonId}/files/`,
      { folder_id: folderId },
      { signal },
    );
  }

  async downloadCorrectionLessonFile(
    lessonId: number,
    fileId: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.CORRECTION_LESSONS}${lessonId}/files/${encodeURIComponent(fileId)}/download/`,
      {},
      { responseType: "blob", signal },
    );
  }

  async getRevision(revisionId: number): Promise<MaterialRevision> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/`,
    );
  }

  async retryRevisionAnalysis(revisionId: number): Promise<MaterialRevision> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/retry-analysis/`,
      {},
    );
  }

  async ignoreRevisionFinding(
    revisionId: number,
    findingId: number,
    reason: string,
  ): Promise<MaterialRevision> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/ignore-finding/`,
      {
        finding_id: findingId,
        reason,
      },
    );
  }

  async requestRevisionChanges(
    revisionId: number,
    message: string,
  ): Promise<MaterialRevision> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/request-changes/`,
      { message },
    );
  }

  async publishRevision(
    revisionId: number,
    payload: MaterialRevisionPublishPayload,
  ): Promise<MaterialRevision> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/publish/`,
      payload,
    );
  }

  async getRevisionPreviewMetadata(
    revisionId: number,
  ): Promise<MaterialRevisionPreviewMetadata> {
    return await this.get(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/preview-metadata/`,
    );
  }

  getRevisionPdfSource(
    revisionId: number,
    variant: "original" | "revised",
    length: number,
  ): MaterialRevisionPdfSource {
    const endpoint = `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVISIONS}${revisionId}/files/${variant}/`;

    return {
      kind: "authenticated-range",
      length,
      rangeChunkSize: 64 * 1024,
      requestRange: async (begin, end, signal) => {
        const response = await this.getResponse<ArrayBuffer>(
          endpoint,
          {},
          {
            headers: {
              Range: `bytes=${begin}-${end - 1}`,
            },
            responseType: "arraybuffer",
            signal,
          },
        );
        const bytes = new Uint8Array(response.data);

        // Keep local/mocked servers that ignore Range safe without changing
        // the production 206 path.
        if (
          response.status === 200 &&
          bytes.byteLength === length &&
          (begin !== 0 || end !== length)
        ) {
          return bytes.slice(begin, end);
        }
        return bytes;
      },
    };
  }

  async getReviewers(): Promise<
    ContentQualityPaginatedResponse<ContentReviewerAccess>
  > {
    return await this.get(ENDPOINTS.ADMIN.CONTENT_QUALITY.REVIEWERS, {
      pageSize: 1000,
    });
  }

  async inviteReviewer(
    payload: ContentReviewerInvitePayload,
  ): Promise<ContentReviewerInviteResponse> {
    return await this.post(ENDPOINTS.ADMIN.CONTENT_QUALITY.REVIEWERS, payload);
  }

  async resendReviewerInvite(
    reviewerId: number,
  ): Promise<ContentReviewerAccess> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVIEWERS}${reviewerId}/resend-invite/`,
      {},
    );
  }

  async revokeReviewer(reviewerId: number): Promise<ContentReviewerAccess> {
    return await this.post(
      `${ENDPOINTS.ADMIN.CONTENT_QUALITY.REVIEWERS}${reviewerId}/revoke/`,
      {},
    );
  }
}

export default AdminContentQualityModule;
